import { supabase } from '../supabase/config';
import { formatDateTime } from '../utils/dateTime';
import { getGoogleMapsUrl } from '../utils/geo';

const participantsStyles = `
<style>
  @keyframes wave {
    0% { transform: translateY(0); }
    15% { transform: translateY(-8px); }
    30% { transform: translateY(0); }
    100% { transform: translateY(0); }
  }
  
  .wave-dot {
    animation: wave 3.5s ease-in-out infinite;
  }
  
  .wave-dot:nth-child(1) { animation-delay: 0s; }
  .wave-dot:nth-child(2) { animation-delay: 0.15s; }
  .wave-dot:nth-child(3) { animation-delay: 0.3s; }
  
  .participant-avatar {
    transition: transform 0.2s ease;
    cursor: pointer;
  }
  
  .participant-avatar:hover {
    transform: scale(1.1);
  }
  
  .participant-avatar:active {
    transform: scale(0.95);
  }
</style>
`;

export const showEventDetailsPanel = async (eventData, photoUrl) => {
  window.currentViewingEventId = eventData.eventId;
  // Store for change detection in realtime updates
const currentUser = window.currentAppUser || window.currentUser;
  window._openEventData = {
    gender_preference: JSON.stringify(eventData.genderPreference || ["man","woman","different"]),
    category: JSON.stringify(Array.isArray(eventData.category) ? eventData.category : eventData.category ? [eventData.category] : ["place"]),
    age_min: eventData.ageMin || 18,
    age_max: eventData.ageMax || 99,
    require_approve: eventData.requireApprove || false,
    people_limit: eventData.peopleLimit,
    pending_participants: JSON.stringify(eventData.pendingParticipants || []),
    _was_participant: (eventData.participants || []).includes(currentUser?.uid),
  };
  
  // 🆕 Hide existing event details panel immediately (no animation)
  const existingPanel = document.getElementById('eventDetailsPanel');
  if (existingPanel) {
    existingPanel.remove(); // Remove immediately, not later
  }
  
  // 🆕 Close chat panel if it's open
  const chatPanel = document.getElementById('chatPanel');
  if (chatPanel) {
    window.closeChatPanel();
    // Wait for chat to close (animation is 300ms)
    await new Promise(resolve => setTimeout(resolve, 350));
  }

// Close user profile panel if it's open (React component - dispatch close event)
  window.dispatchEvent(new CustomEvent('closeUserProfilePanel'));
  // 🆕 Fetch organizer data first
  let organizer;
  
  if (eventData.creatorId === currentUser?.uid) {
    organizer = {
      name: currentUser.profile.name,
      profilePicture: currentUser.profile.profilePicture
    };
  } else {
    try {
      const supabaseClient = window.supabase || supabase;
      const { data, error } = await supabaseClient
        .from('users')
        .select('name, profile_picture')
        .eq('id', eventData.creatorId)  // Make sure this is 'id' not 'uid'
        .single();
      
      if (!error && data) {
        organizer = {
          name: data.name,
          profilePicture: data.profile_picture
        };
      } else {
        organizer = { name: eventData.createdBy || 'Unknown', profilePicture: null };
      }
    } catch (err) {
      organizer = { name: eventData.createdBy || 'Unknown', profilePicture: null };
    }
  }
  
// 🆕 Fetch participants data (EXCLUDING organizer)
  const participantIds = eventData.participants || [eventData.creatorId];
  const otherParticipantIds = participantIds.filter(id => id !== eventData.creatorId);
  const participantsData = [];
  
  // SIMPLE: Always show maximum 5 avatars
  const maxAvatars = 5;
  const hasJoiners = otherParticipantIds.length > 0;
// Show +N if more than 5 joiners
const showMoreCounter = otherParticipantIds.length > maxAvatars;
// Calculate event status FIRST (before showWaitingDots)
// ✅ USE MASTER STATUS CALCULATOR (handles timezone correctly)
const statusForParticipants = window.calculateEventStatus(eventData.startDateTime, eventData.endDateTime);
const hasStarted = statusForParticipants.hasStarted;
const hasEnded = statusForParticipants.hasEnded;
const endDate = statusForParticipants.endDate; // ← ADD THIS

const participantCount = eventData.participants?.length || 1;
const limitReached = eventData.peopleLimit && participantCount >= eventData.peopleLimit;

const showWaitingCircle = otherParticipantIds.length <= maxAvatars && !limitReached;
const animateWaiting = showWaitingCircle && !hasEnded;
  
  // Fetch only first 5
  const visibleCount = Math.min(otherParticipantIds.length, maxAvatars);
  
for (let i = 0; i < visibleCount; i++) {
    const participantId = otherParticipantIds[i];
    try {
      const supabaseClient = window.supabase || supabase;
      const { data, error } = await supabaseClient
        .from('users')
        .select('id, name, profile_picture')
        .eq('id', participantId)
        .single();
      
      if (!error && data) {
        participantsData.push(data);
      }
    } catch (err) {
      console.warn('⚠️ Error fetching participant (user may have been deleted):', participantId);
    }
  }
  
  // CRITICAL: Use actual fetched participant count, not database count
  const actualParticipantCount = participantsData.length + 1; // +1 for organizer
  const dbParticipantCount = eventData.participants?.length || 1;
  
  // If counts don't match, clean up the database
  if (actualParticipantCount !== dbParticipantCount) { 
    // Build valid participant list (only users that exist)
    const validParticipantIds = [eventData.creatorId, ...participantsData.map(p => p.id)];
    
    try {
      // Update database with cleaned list
      const supabaseClient = window.supabase || supabase;
      await supabaseClient
        .from('events')
        .update({ participants: validParticipantIds })
        .eq('event_id', eventData.eventId);
      
      // Update eventData for display
      eventData.participants = validParticipantIds;
      
// Update marker inline after cleanup
      const placeId = eventData.placeId;
      if (placeId && window.eventMarkersRef?.current) {
        const marker = window.eventMarkersRef?.current.find(m => {
          const content = m.content || m.getElement?.() || m._element;
          if (!content) return false;
          const directPlaceId = content.dataset?.placeId;
          const childPlaceId = content.querySelector?.('[data-place-id]')?.dataset?.placeId;
          const markerPlaceId = directPlaceId || childPlaceId;
          return markerPlaceId === placeId;
        });
        
        if (marker) {
          const markerElement = marker.content || marker.getElement?.() || marker._element;
          if (markerElement) {
            const participantCountSpans = markerElement.querySelectorAll('span[style*="font-weight: 600"]');
            participantCountSpans.forEach(countSpan => {
              if (countSpan.textContent.includes('/')) {
                const newCount = validParticipantIds.length;
                const limit = eventData.peopleLimit;
                countSpan.textContent = limit ? `${newCount} / ${limit}` : `${newCount} / ∞`;
              }
            });
          }
        }
      }
    } catch (cleanupError) {
      console.error('Error cleaning up participants:', cleanupError);
    }
  }
  
  const remainingCount = otherParticipantIds.length - visibleCount;
  const isOngoing = hasStarted && !hasEnded && endDate;
  
  // USE MASTER STATUS CALCULATOR
  const initialStatus = window.calculateEventStatus(eventData.startDateTime, eventData.endDateTime);
  let statusColor = initialStatus.statusColor;
  let statusText = initialStatus.statusText;
  let shouldPulse = initialStatus.shouldPulse;

    // Get current user from window
    const currentUserData = window.currentAppUser;

    // Format dates for display
const startDisplay = formatDateTime(eventData.startDateTime);
const endDisplay = formatDateTime(eventData.endDateTime);

console.log('Event data:', eventData);
console.log('Creator field:', eventData.odCreatedBy, eventData.createdBy, eventData.creatorId);

    const panel = document.createElement('div');
panel.id = 'eventDetailsPanel';
panel.style.cssText = `
  position: fixed;
  top: 79px;
  right: -400px;
  width: 380px;
  max-width: calc(100vw - 4px);
  height: calc(100vh - 79px - var(--footer-height, 50px));
  border-bottom-left-radius: 20px;
  border-top-left-radius: 20px;
  background: white;
  box-shadow: -4px 0 12px rgba(0,0,0,0.15);
  border-left: 3px solid ${statusColor};
  z-index: 1000;
  transition: right 0.3s ease-out;
  overflow-y: auto;
`;

// Store event data for editing - INCLUDING original values
panel.dataset.eventId = eventData.eventId;
panel.dataset.originalTitle = eventData.title;
panel.dataset.originalDescription = eventData.description;
panel.dataset.isCustom = eventData.placeId.startsWith('custom_');
panel.dataset.hasStarted = hasStarted;
panel.dataset.startDateTime = eventData.startDateTime;
panel.dataset.endDateTime = eventData.endDateTime;
panel.dataset.placePhotoUrl = eventData.placePhotoUrl || '';
panel.dataset.customLabel = eventData.customLabel || (eventData.placeId && eventData.placeId.startsWith('custom_') ? eventData.placeName : '') || '';
  // Store openedFromList in panel dataset for persistence
  panel.dataset.openedFromList = window.openedFromList ? 'true' : 'false';
panel.dataset.requireApprove = eventData.requireApprove || false;
  panel.dataset.genderPreference = JSON.stringify(eventData.genderPreference || eventData.gender_preference || ['man', 'woman', 'different']);
  panel.dataset.category = JSON.stringify(Array.isArray(eventData.category) ? eventData.category : (eventData.category ? [eventData.category] : ['place']));
  panel.dataset.ageMin = eventData.ageMin || 18;
  panel.dataset.ageMax = eventData.ageMax || 99;

panel.innerHTML = `
  ${participantsStyles}
  <!-- Scrollable Content Area -->
  <div style="
    height: calc(100% - 90px);
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 20px;
  ">
<!-- Share & Close buttons container -->
    <!-- Share & Close buttons container -->
    <div style="
      display: flex;
      align-items: center;
      gap: 4px;
      float: right;
    ">
<!-- Share button (hidden for archived events) -->
      ${!eventData.archived ? `
      <button 
        onclick="window.shareEvent('${eventData.eventId}')"
        style="
          background: none;
          border: none;
          color: #5f6368;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background 0.2s;
        "
        onmouseover="this.style.background='#f1f3f4'"
        onmouseout="this.style.background='none'"
        title="Share event"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="18" cy="5" r="3"></circle>
          <circle cx="6" cy="12" r="3"></circle>
          <circle cx="18" cy="19" r="3"></circle>
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
        </svg>
      </button>

      <!-- Separator -->
      <div style="width: 1px; height: 20px; background: #dadce0;"></div>
      ` : ''}

      <!-- Close button -->
      <button 
        onclick="window.cleanupEventRealtimeUpdates(); window.closeAllPanels();"
        style="
          background: none;
          border: none;
          color: #5f6368;
          cursor: pointer;
          padding: 0;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          transition: background 0.2s;
        "
        onmouseover="this.style.background='#f1f3f4'"
        onmouseout="this.style.background='none'"
        title="Close"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

<!-- Header -->
<div style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px; ${window.openedFromList ? '' : 'padding-right: 40px;'}">
  ${window.openedFromList ? `
    <button 
      onclick="
        event.stopPropagation();
        const eventData = ${JSON.stringify(eventData).replace(/"/g, '&quot;')};
        window.closeAllPanels();
        setTimeout(() => {
          showEventsListPanel('${eventData.placeId}', '${eventData.placeName.replace(/'/g, "\\'")}', '${eventData.placeAddress.replace(/'/g, "\\'")}', ${eventData.latitude || 'undefined'}, ${eventData.longitude || 'undefined'});
          window.openedFromList = false;
        }, 350);
      "
      style="
        background: none;
        border: none;
        color: #5f6368;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        transition: background 0.2s;
        flex-shrink: 0;
      "
      onmouseover="this.style.background='#f1f3f4'"
      onmouseout="this.style.background='none'"
      title="Back to list"
    >
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="19" y1="12" x2="5" y2="12"></line>
        <polyline points="12 19 5 12 12 5"></polyline>
      </svg>
    </button>
  ` : ''}
<div style="display: flex; align-items: center; gap: 12px;">
  <h2 style="margin: 0; font-size: 24px; font-weight: 500; color: #202124;">
    Event Details
  </h2>
<!-- Organizer Crown Badge OR Participant Checkmark Badge -->
${(() => {
  const isOrganizer = eventData.creatorId === window.currentAppUser?.uid;
  const isParticipant = eventData.participants?.includes(window.currentAppUser?.uid) && !isOrganizer;
  
  if (isOrganizer) {
    return `
      <div style="
        width: 25px;
        height: 25px;
        background: #e1e1e1fa;
        border: 2px solid #00a3b7;
        border-radius: 30%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
        margin-bottom: -3px;
        flex-shrink: 0;
      ">
        <span style="font-size: 16px; line-height: 1; margin-top: -2px;">👑</span>
      </div>
    `;
  } else if (isParticipant) {
    return `
      <div style="
        width: 25px;
        height: 25px;
        background: white;
        border: 2px solid rgba(52, 168, 83, 1);
        border-radius: 30%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
        margin-bottom: -3px;
        flex-shrink: 0;
      ">
        <span style="font-size: 16px; line-height: 1; color: green;">✓</span>
      </div>
    `;
  }
  return '';
})()}
</div>
</div>

    <!-- Event Image and Title with status border and shadow -->
<div style="position: relative; margin-bottom: 20px;">
  ${shouldPulse ? `
    <style>
      @keyframes placeCardPulse {
        0% { 
          transform: scale(1);
          opacity: 0.6;
        }
        50% { 
          transform: scale(1.03);
          opacity: 0.3;
        }
        100% { 
          transform: scale(1.06);
          opacity: 0;
        }
      }
    </style>
<div data-place-pulse style="
  position: absolute;
  inset: -6px;
  border-radius: 14px;
  border: 2px solid ${statusColor};
  animation: placeCardPulse 2s ease-in-out infinite;
  pointer-events: none;
  z-index: -1;
  display: ${shouldPulse ? 'block' : 'none'};
"></div>
  ` : ''}
<div data-place-card style="display: flex; align-items: start; gap: 12px; padding: 12px; background: #f8f9fa; border-radius: 12px; border: 2px solid ${statusColor}; box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2), 0 4px 12px rgba(0, 0, 0, 0.15); position: relative;">
${photoUrl ? `<img 
    src="${photoUrl}" 
    alt="${eventData.placeName}" 
    style="width: 48px; height: 48px; border-radius: 8px; object-fit: cover; flex-shrink: 0;"
  />` : `<div style="width: 48px; height: 48px; border-radius: 8px; flex-shrink: 0; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); display: flex; align-items: center; justify-content: center; font-size: 24px;">📍</div>`}
<div style="flex: 1; min-width: 0; padding-right: 8px;">
  <h3 style="margin: 0 0 6px 0; font-size: 16px; font-weight: 600; color: #202124; line-height: 1.3; word-wrap: break-word;">
    ${eventData.placeName}
  </h3>
  <div style="display: flex; gap: 4px; align-items: start;">
    <span style="font-size: 12px; color: #5f6368; flex-shrink: 0;">📍</span>
    <p style="margin: 0; font-size: 12px; color: #5f6368; word-wrap: break-word; overflow-wrap: break-word; line-height: 1.4; flex: 1;">
      ${eventData.placeId.startsWith('custom_') ? `${eventData.latitude.toFixed(6)}, ${eventData.longitude.toFixed(6)}` : eventData.placeAddress}
    </p>
  </div>
</div>
<button
  onclick="
  console.log('Opening Google Maps with:', {
    placeId: '${eventData.placeId}',
    lat: ${eventData.latitude},
    lng: ${eventData.longitude}
  });
  (window.openExternal||window.open)('${getGoogleMapsUrl(eventData.placeId, eventData.latitude, eventData.longitude)}', '_blank');
"
  style="
    width: 36px;
    height: 36px;
    background: #1967D2;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.15);
    transition: all 0.2s;
    padding: 4px;
    flex-shrink: 0;
  "
  onmouseover="this.style.background='#1557B0'; this.style.boxShadow='0 3px 8px rgba(0,0,0,0.25)'; this.style.transform='scale(1.05)'"
  onmouseout="this.style.background='#1967D2'; this.style.boxShadow='0 2px 4px rgba(0,0,0,0.15)'; this.style.transform='scale(1)'"
  title="View in Google Maps"
>
<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="3 11 22 2 13 21 11 13 3 11"></polygon>
  </svg>
</button>
</div>
  </div>

<!-- Locate Button and Join/Chat Button -->
<div style="
  display: flex;
  gap: 12px;
  justify-content: center;
  margin: 20px 0;
">
  ${(() => {
    const isOrganizer = eventData.creatorId === window.currentAppUser?.uid;
    const isParticipant = eventData.participants?.includes(window.currentAppUser?.uid);
    const hasPendingRequest = eventData.pendingRequests?.includes(window.currentAppUser?.uid);
    const participantCount = eventData.participants?.length || 1;
    const limitReached = eventData.peopleLimit && participantCount >= eventData.peopleLimit;
    
    // Check if event has finished (using status calculated above)
    const eventFinished = hasEnded;
    
    // Determine button state
    let buttonHTML = '';
    
    // Don't show any join/chat button for finished events (unless you're organizer/participant)
    if (!eventFinished || isOrganizer || isParticipant) {
      if (isOrganizer) {
        // Show Chat button for organizer
        buttonHTML = `
          <button
            id="chatButton_${eventData.eventId}"
  onclick="
    window.openedFromChatsList = false;
    window.openedEventDetailsFromChat = false;
    window.openChatAndClearIndicator('${eventData.eventId}', '${eventData.title.replace(/'/g, "\\'")}')"
            style="
              background: white;
              color: black;
              border: 2px solid rgba(97, 97, 97, 1);
              border-radius: 24px;
              padding: 12px 24px;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
              transition: all 0.3s ease;
              flex: 1;
            "
            onmouseover="this.style.transform='scale(1.02)'; this.style.boxShadow='3px 3px 8px rgba(0, 0, 0, 0.5)'"
            onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='2px 2px 6px rgba(0, 0, 0, 0.4)'"
          >
            Chat
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </button>
        `;
      } else if (isParticipant) {
        // Show Chat button for participants
        buttonHTML = `
          <button
            id="chatButton_${eventData.eventId}"
  onclick="
    window.openedFromChatsList = false;
    window.openedEventDetailsFromChat = false;
    window.openChatAndClearIndicator('${eventData.eventId}', '${eventData.title.replace(/'/g, "\\'")}')"
            style="
              background: white;
              color: black;
              border: 2px solid rgba(97, 97, 97, 1);
              border-radius: 24px;
              padding: 12px 24px;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
              transition: all 0.3s ease;
              flex: 1;
            "
            onmouseover="this.style.transform='scale(1.02)'; this.style.boxShadow='3px 3px 8px rgba(0, 0, 0, 0.5)'"
            onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='2px 2px 6px rgba(0, 0, 0, 0.4)'"
          >
            Chat
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
          </button>
        `;
      } else if (hasPendingRequest) {
        // Show Pending button
        buttonHTML = `
          <button
            disabled
            style="
              background: white;
              color: black;
              border: 2px solid rgb(97, 97, 97);
              border-radius: 24px;
              padding: 12px 24px;
              font-size: 14px;
              font-weight: 600;
              cursor: not-allowed;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
              flex: 1;
              opacity: 0.5;
            "
          >
            Pending
          </button>
        `;
} else if (!limitReached && !eventData.removedFromEvent) {
        // Show Join button
        buttonHTML = `
          <button
            id="joinButton_${eventData.eventId}"
            onclick="window.joinEvent('${eventData.eventId}')"
            style="
              background: white;
              color: black;
              border: 2px solid rgba(97, 97, 97, 1);
              border-radius: 24px;
              padding: 12px 24px;
              font-size: 14px;
              font-weight: 600;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
              transition: all 0.3s ease;
              flex: 1;
            "
            onmouseover="this.style.transform='scale(1.02)'; this.style.boxShadow='3px 3px 8px rgba(0, 0, 0, 0.5)'"
            onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='2px 2px 6px rgba(0, 0, 0, 0.4)'"
          >
            Join
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="8.5" cy="7" r="4"></circle>
              <line x1="20" y1="8" x2="20" y2="14"></line>
              <line x1="23" y1="11" x2="17" y2="11"></line>
            </svg>
          </button>
        `;
} else if (!eventData.removedFromEvent) {
        // Limit reached
        buttonHTML = `
          <button
            disabled
            style="
              background: transparent;
              color: black;
              border: 2px dashed rgb(97, 97, 97);
              border-radius: 24px;
              padding: 12px 24px;
              font-size: 14px;
              font-weight: 600;
              cursor: not-allowed;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              flex: 1;
              opacity: 0.7;
            "
          >
            Full
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
          </button>
        `;
      }
    }
    
    return buttonHTML;
  })()}
  
<!-- Locate Button - Hidden for removed users -->
  ${!eventData.removedFromEvent ? `
  <button
      onclick="
    if (window.innerWidth < 800) {
      const edp = document.getElementById('eventDetailsPanel');
      if (edp) { edp.style.right = '-400px'; setTimeout(() => edp.remove(), 300); }
      const cp = document.getElementById('chatPanel');
      if (cp) { cp.style.right = '-400px'; setTimeout(() => cp.remove(), 300); }
      const elp = document.getElementById('eventsListPanel');
if (elp) { elp.style.right = '-400px'; setTimeout(() => elp.remove(), 300); }
if (window.setIsProfilePanelOpen) window.setIsProfilePanelOpen(false);
    }
    if (window.leafletMap) {
      setTimeout(() => window.leafletMap.flyTo([${eventData.latitude}, ${eventData.longitude}], 18, { duration: 1.5 }), window.innerWidth < 800 ? 200 : 0);
    }
  "
    style="
      background: rgba(33, 33, 33, 1);
      color: white;
      border: none;
      border-radius: 24px;
      padding: 12px 24px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      box-shadow: 2px 2px 6px rgba(0, 0, 0, 0.4);
      transition: all 0.3s ease;
      flex: 1;
    "
    onmouseover="this.style.transform='scale(1.02)'; this.style.boxShadow='3px 3px 8px rgba(0, 0, 0, 0.5)'"
    onmouseout="this.style.transform='scale(1)'; this.style.boxShadow='2px 2px 6px rgba(0, 0, 0, 0.4)'"
  >
    Locate
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <circle cx="11" cy="11" r="8"></circle>
      <path d="m21 21-4.35-4.35"></path>
    </svg>
  </button>
  ` : ''}
</div>

<!-- Requires Approve Indicator (only show to non-organizers and non-participants) -->
${eventData.requireApprove 
  && eventData.creatorId !== window.currentAppUser?.uid 
  && !eventData.participants?.includes(window.currentAppUser?.uid) ? `
  <div data-requires-approval style="
    text-align: center;
    padding: 12px 16px;
    margin: 0 20px 16px 20px;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  ">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#856404" stroke-width="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
      <path d="M9 11l3 3 5-5"></path>
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" stroke-linecap="round"></path>
    </svg>
    <span style="
      font-size: 13px;
      font-weight: 600;
      color: #856404;
    ">Requires Approval</span>
  </div>
` : ''}


<!-- Countdown Timer (only if event hasn't started and is within 30 days) -->
${(() => {
  const startDate = initialStatus.startDate;
  const now = initialStatus.now;
  const timeUntilStart = startDate - now;
  const daysUntilStart = timeUntilStart / (1000 * 60 * 60 * 24);
  
  // ✅ Only show countdown if event HASN'T STARTED yet AND is within 30 days
  const showCountdown = !hasStarted && daysUntilStart > 0 && daysUntilStart <= 30;
  
  if (showCountdown) {
    const uniqueEventId = `${eventData.placeId}_${eventData.startDateTime}`.replace(/[^a-zA-Z0-9]/g, '_');
    return `
      <div
        data-countdown-timer 
        id="countdown_details_${uniqueEventId}"
        data-event-time="${eventData.startDateTime}"
        style="
          text-align: center;
          padding: 12px 0;
          margin-bottom: 20px;
        "
      >
        <div style="
          display: flex;
          justify-content: center;
          align-items: flex-end;
          gap: 8px;
        ">
          <div style="display: flex; flex-direction: column; align-items: center;">
            <span style="
              font-size: 20px;
              font-weight: 600;
              color: #202124;
              letter-spacing: 1px;
              min-width: 30px;
              display: inline-block;
            " id="countdown_details_${uniqueEventId}_days">00</span>
            <span style="
              font-size: 10px;
              color: #5f6368;
              font-weight: 500;
              margin-top: 2px;
              text-transform: uppercase;
            ">days</span>
          </div>
          
          <span style="
            font-size: 20px;
            font-weight: 600;
            color: #202124;
            margin-bottom: 16px;
          ">:</span>
          
          <div style="display: flex; flex-direction: column; align-items: center;">
            <span style="
              font-size: 20px;
              font-weight: 600;
              color: #202124;
              letter-spacing: 1px;
              min-width: 30px;
              display: inline-block;
            " id="countdown_details_${uniqueEventId}_hours">00</span>
            <span style="
              font-size: 10px;
              color: #5f6368;
              font-weight: 500;
              margin-top: 2px;
              text-transform: uppercase;
            ">hours</span>
          </div>
          
          <span style="
            font-size: 20px;
            font-weight: 600;
            color: #202124;
            margin-bottom: 16px;
          ">:</span>
          
          <div style="display: flex; flex-direction: column; align-items: center;">
            <span style="
              font-size: 20px;
              font-weight: 600;
              color: #202124;
              letter-spacing: 1px;
              min-width: 30px;
              display: inline-block;
            " id="countdown_details_${uniqueEventId}_minutes">00</span>
            <span style="
              font-size: 10px;
              color: #5f6368;
              font-weight: 500;
              margin-top: 2px;
              text-transform: uppercase;
            ">minutes</span>
          </div>
          
          <span style="
            font-size: 20px;
            font-weight: 600;
            color: #202124;
            margin-bottom: 16px;
          ">:</span>
          
          <div style="display: flex; flex-direction: column; align-items: center;">
            <span style="
              font-size: 20px;
              font-weight: 600;
              color: #202124;
              letter-spacing: 1px;
              min-width: 30px;
              display: inline-block;
            " id="countdown_details_${uniqueEventId}_seconds">00</span>
            <span style="
              font-size: 10px;
              color: #5f6368;
              font-weight: 500;
              margin-top: 2px;
              text-transform: uppercase;
            ">seconds</span>
          </div>
        </div>
      </div>
    `;
  }
  return '';
})()}

<!-- Event Title -->
<div style="margin-bottom: 16px; position: relative; display: flex; align-items: center;">
  <h3 data-event-title style="margin: 0; font-size: 20px; font-weight: 600; color: ${statusColor}; flex: 1; padding-right: 30px;">
    ${eventData.title}
  </h3>
</div>

<!-- Description -->
<div style="margin-bottom: 20px; position: relative;">
  <label style="display: block; font-size: 12px; font-weight: 600; color: #5f6368; margin-bottom: 8px; text-transform: uppercase;">
    Description
  </label>
  <div data-description-container style="position: relative;">
    ${(() => {
      const maxLength = 500;
      
      let cleanDescription = eventData.description
        .replace(/^\s+/, '')
        .replace(/\s+$/, '');
      
      const isLong = cleanDescription.length > maxLength;
      const shortText = isLong ? cleanDescription.substring(0, maxLength) : cleanDescription;
      const uniqueId = `desc_${eventData.placeId}_${Date.now()}`;
      
      if (isLong) {
        return `
          <div style="font-size: 14px; color: #202124; line-height: 1.5; margin: 0; padding: 0; padding-right: 30px;">
            <span 
              id="${uniqueId}_short" 
              onclick="
                document.getElementById('${uniqueId}_short').style.display = 'none';
                document.getElementById('${uniqueId}_full_container').style.display = 'inline';
              "
              style="
                word-wrap: break-word; 
                overflow-wrap: break-word; 
                white-space: pre-wrap;
                cursor: pointer;
                user-select: none;
                margin: 0;
                padding: 0;
              "
              onmouseover="this.style.opacity='0.85'"
              onmouseout="this.style.opacity='1'"
            >${shortText}... <span
                style="
                  color: #1a73e8;
                  margin-left: 4px;
                  display: inline-flex;
                  align-items: center;
                  vertical-align: middle;
                "
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </span>
            </span>
            <span 
              id="${uniqueId}_full_container"
              style="
                display: none; 
                word-wrap: break-word; 
                overflow-wrap: break-word; 
                white-space: pre-wrap;
                margin: 0;
                padding: 0;
              "
            >${cleanDescription}<span
                onclick="
                  event.stopPropagation();
                  document.getElementById('${uniqueId}_short').style.display = 'inline';
                  document.getElementById('${uniqueId}_full_container').style.display = 'none';
                "
                style="
                  color: #1a73e8;
                  margin-left: 6px;
                  display: inline-flex;
                  align-items: center;
                  vertical-align: text-bottom;
                  transition: all 0.2s;
                  cursor: pointer;
                  user-select: none;
                "
                onmouseover="this.style.opacity='0.7'"
                onmouseout="this.style.opacity='1'"
              ><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="18 15 12 9 6 15"></polyline></svg></span></span>
          </div>
        `;
      } else {
        return `
          <pre data-event-description style="margin: 0; padding: 0; padding-right: 30px; font-size: 14px; color: #202124; line-height: 1.5; word-wrap: break-word; overflow-wrap: break-word; white-space: pre-wrap; font-family: inherit;">${cleanDescription}</pre>
        `;
      }
    })()}
  </div>
</div>

<!-- Time Info -->
<div data-when-section style="
  background: #f8f9fa;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
">
  <div data-start-row style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px; position: relative;">
    <div style="
      width: 36px;
      height: 36px;
      background: #e8f5e9;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    ">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#34a853" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
    </div>
    <div style="flex: 1; min-width: 0;">
      <p style="margin: 0; font-size: 11px; color: #5f6368; text-transform: uppercase; letter-spacing: 0.5px;">Starts</p>
      <p data-start-value style="margin: 0; font-size: 14px; color: #202124; font-weight: 500;">${startDisplay}</p>
    </div>
  </div>
  <div data-end-row style="display: flex; align-items: center; gap: 12px; position: relative;">
    <div style="
      width: 36px;
      height: 36px;
      background: #fce8e6;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    ">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ea4335" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
    </div>
    <div style="flex: 1; min-width: 0;">
      <p style="margin: 0; font-size: 11px; color: #5f6368; text-transform: uppercase; letter-spacing: 0.5px;">Ends</p>
      <p data-end-value style="margin: 0; font-size: 14px; color: #202124; font-weight: 500;">${endDisplay}</p>
    </div>
  </div>
</div>

    <!-- Status Circle -->
    <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 20px;">
      <div style="position: relative; width: 14px; height: 14px; display: flex; align-items: center; justify-content: center;">
        ${shouldPulse ? `
          <style>
            @keyframes statusPulse {
              0% { transform: scale(1); opacity: 0.6; }
              50% { transform: scale(1.4); opacity: 0.3; }
              100% { transform: scale(1.8); opacity: 0; }
            }
          </style>
          <div data-status-pulse style="
            position: absolute;
            width: 14px;
            height: 14px;
            border: 3px solid ${statusColor};
            border-radius: 50%;
            opacity: 0.4;
            animation: statusPulse 2s ease-in-out infinite;
          "></div>
        ` : ''}
<div data-status-circle style="
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${statusColor};
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  position: relative;
  z-index: 1;
"></div>
      </div>
      <span data-status-text style="font-size: 14px; font-weight: 600; color: ${statusColor};">
        ${statusText}
      </span>
    </div>

<!-- Gender Preference Indicator -->
${(() => {
  const genderInfo = window.getGenderPreferenceIcon(eventData.genderPreference || eventData.gender_preference);
  return `
<div data-gender-badge style="
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 12px;
      background: ${genderInfo.color}15;
      border-radius: 20px;
      margin-right: 8px;
    ">
      <span style="font-size: 14px;">${genderInfo.icon}</span>
      <span style="font-size: 12px; font-weight: 500; color: ${genderInfo.color};">${genderInfo.label}</span>
    </div>
  `;
})()}

<!-- Category Badges -->
${(() => {
  const categories = Array.isArray(eventData.category) ? eventData.category : (eventData.category ? [eventData.category] : ['place']);
const catLabels = {
    // New categories
    food_drink:    { icon: '🍽️', label: 'Food & Drink' },
    nightlife:     { icon: '🌙', label: 'Nightlife' },
    outdoors:      { icon: '🌿', label: 'Outdoors' },
    active:        { icon: '⚡', label: 'Active' },
    shopping:      { icon: '🛍️', label: 'Shopping' },
    entertainment: { icon: '🎭', label: 'Entertainment' },
    // Legacy keys (old events in DB)
    restaurant: { icon: '🍽️', label: 'Food & Drink' },
    cafe:       { icon: '🍽️', label: 'Food & Drink' },
    bar:        { icon: '🍽️', label: 'Food & Drink' },
    fast_food:  { icon: '🍽️', label: 'Food & Drink' },
    park:       { icon: '🌿', label: 'Outdoors' },
    sports_centre: { icon: '⚡', label: 'Active' },
    gym:        { icon: '⚡', label: 'Active' },
    shop:       { icon: '🛍️', label: 'Shopping' },
    cinema:     { icon: '🎭', label: 'Entertainment' },
    nightclub:  { icon: '🌙', label: 'Nightlife' },
    beach:      { icon: '🌿', label: 'Outdoors' },
    attraction: { icon: '🎭', label: 'Entertainment' },
    hotel:      { icon: '🎭', label: 'Entertainment' },
    museum:     { icon: '🎭', label: 'Entertainment' },
    place:      { icon: '🎭', label: 'Entertainment' },
  };
  return '<div data-category-badges style="display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px; margin-bottom: 4px;">' +
    categories.map(function(c) {
      var info = catLabels[c] || catLabels.entertainment;
      return '<span style="display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; background: #f1f3f4; border-radius: 16px; font-size: 12px; color: #202124; font-weight: 500;"><span>' + info.icon + '</span>' + info.label + '</span>';
    }).join('') + 
  '</div>';
})()}

<!-- Age Limit Badge -->
${(() => {
  const ageMin = eventData.ageMin || 18;
  const ageMax = eventData.ageMax || 99;
  if (ageMin === 18 && ageMax === 99) return '';
  return '<div data-age-badge style="display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; background: #fff3e0; border-radius: 16px; font-size: 12px; color: #e65100; font-weight: 500; margin-top: 6px;"><span>🕵</span> Age ' + ageMin + ' - ' + ageMax + '</div>';
})()}

<!-- Participants -->
<div style="margin: 20px 0;">
  <div style="
    display: flex; 
    align-items: center; 
    justify-content: space-between; 
    margin-bottom: 12px;
    gap: 8px;
  ">
    <h4 style="
      font-size: 12px;
      font-weight: 600;
      color: #5f6368;
      margin: 0;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      display: flex;
      align-items: center;
      gap: 8px;
      flex: 1;
    ">
      PARTICIPANTS
      <span data-participants-count style="
        font-size: 12px; 
        color: rgba(59, 59, 59, 1); 
        background: rgba(224, 224, 224, 1); 
        padding: 2px 8px; 
        border-radius: 12px;
        white-space: nowrap;
      ">
        ${(() => {
          const participantCount = eventData.participants?.length || 1;
          const limit = eventData.peopleLimit;
          
          if (limit === null || limit === undefined) {
            return `${participantCount} / ∞`;
          } else {
            return `${participantCount} / ${limit}`;
          }
        })()}
      </span>
    </h4>
    
    <!-- List icon button (hidden in edit mode) -->
    <button
      data-participants-list-btn
      onclick="window.showAllParticipantsListFresh('${eventData.eventId}')"
      style="
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
        display: flex;
        align-items: center;
        transition: transform 0.2s;
        flex-shrink: 0;
      "
      onmouseover="this.style.transform='scale(1.15)';"
      onmouseout="this.style.transform='scale(1)';"
      title="View all participants"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5f6368" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="8" y1="6" x2="21" y2="6"></line>
        <line x1="8" y1="12" x2="21" y2="12"></line>
        <line x1="8" y1="18" x2="21" y2="18"></line>
        <line x1="3" y1="6" x2="3.01" y2="6"></line>
        <line x1="3" y1="12" x2="3.01" y2="12"></line>
        <line x1="3" y1="18" x2="3.01" y2="18"></line>
      </svg>
    </button>
  </div>
  
  <div data-participants-row style="
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: nowrap;
  ">
    ${await (async () => {
      const participants = participantsData.map(async participant => {
        const isOnline = await window.getUserOnlineStatus(participant.id);
        const statusColor = isOnline ? '#34a853' : '#9e9e9e';
        const borderColor = isOnline ? '#34a853' : '#9e9e9e';
        const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(participant.name || 'User')}&background=e8eaed&color=5f6368&size=40`;
        
        return `
<div 
  class="participant-avatar"
  onclick="window.showUserProfilePanel('${participant.id}', 'eventDetails')"
  style="flex-shrink: 0; position: relative;"
>
            <img 
              src="${participant.profile_picture || fallbackAvatar}"
              alt="${participant.name}"
              data-user-id="${participant.id}"
              style="
                width: 40px;
                height: 40px;
                border-radius: 50%;
                object-fit: cover;
                border: 2px solid ${borderColor};
              "
              onerror="this.src='${fallbackAvatar}'"
            />
            <!-- Online Status Dot -->
            <div style="
              position: absolute;
              bottom: 0;
              right: 0;
              width: 12px;
              height: 12px;
              background: ${statusColor};
              border: 2px solid white;
              border-radius: 50%;
              box-shadow: 0 1px 2px rgba(0,0,0,0.2);
            "></div>
          </div>
        `;
      });
      
      const resolvedParticipants = await Promise.all(participants);
      return resolvedParticipants.join('');
    })()}
    
    ${(() => {
      const participantCount = eventData.participants?.length || 1;
      const limitReached = eventData.peopleLimit && participantCount >= eventData.peopleLimit;
      
      if (showWaitingCircle) {
            const dotClass = animateWaiting ? 'class="wave-dot"' : '';
            const title = hasEnded ? 'Event finished' : 'Waiting for joiners...';
        return `
          <div 
            style="
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background: #dddddd;
              border: 2px solid #e8eaed;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 3px;
              cursor: default;
              flex-shrink: 0;
            "
            title="${title}"
          >
            <div ${dotClass} style="width: 4px; height: 4px; border-radius: 50%; background: #5f6368;"></div>
            <div ${dotClass} style="width: 4px; height: 4px; border-radius: 50%; background: #5f6368;"></div>
            <div ${dotClass} style="width: 4px; height: 4px; border-radius: 50%; background: #5f6368;"></div>
          </div>
        `;
      } else if (showMoreCounter) {
        return `
          <div 
            onclick='window.showAllParticipantsList(${JSON.stringify(eventData)})'
            style="
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background: #dddddd;
              border: 2px solid #e8eaed;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              flex-shrink: 0;
              font-size: 12px;
              font-weight: 600;
              color: #202124;
            "
            title="View all ${otherParticipantIds.length} participants"
          >
            +${remainingCount}
          </div>
        `;
      }
      return '';
    })()}
  </div>
</div>

<!-- Organizer -->
<div style="margin: 20px 0;">
  <h4 style="
    font-size: 12px;
    font-weight: 600;
    color: #5f6368;
    margin: 0 0 8px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  ">ORGANIZED BY</h4>
  <div style="display: flex; align-items: center; gap: 12px;">
    ${await (async () => {
      const isCurrentUser = eventData.creatorId === window.currentAppUser?.uid;
      const isOnline = await window.getUserOnlineStatus(eventData.creatorId);
      const statusColor = isOnline ? '#34a853' : '#9e9e9e';
      let borderColor = '#e8eaed'; // default gray
      
      if (isCurrentUser) {
        borderColor = '#00a3b7'; // blue for current user
      } else if (isOnline) {
        borderColor = '#34a853'; // green if online
      } else {
        borderColor = '#9e9e9e'; // darker gray if offline
      }
      
      const fallbackUrl = `https://ui-avatars.com/api/?name=${encodeURIComponent(organizer.name || 'User')}&background=e8eaed&color=5f6368&size=40`;
      
      return `
<div 
  class="participant-avatar"
  style="
    position: relative; 
    cursor: pointer;
    transition: transform 0.2s ease;
  " 
  onclick="window.showUserProfilePanel('${eventData.creatorId}', 'eventDetails')"
>
          <img 
            src="${organizer.profilePicture || fallbackUrl}"
            alt="${organizer.name}"
            data-user-id="${eventData.creatorId}"
            style="
              width: 40px;
              height: 40px;
              border-radius: 50%;
              object-fit: cover;
              border: 2px solid ${borderColor};
            "
            onerror="this.src='${fallbackUrl}'"
          />
          <!-- Online Status Dot -->
          <div style="
            position: absolute;
            bottom: 0;
            right: 0;
            width: 12px;
            height: 12px;
            background: ${statusColor};
            border: 2px solid white;
            border-radius: 50%;
            box-shadow: 0 1px 2px rgba(0,0,0,0.2);
          "></div>
        </div>
        <p style="
          font-size: 14px;
          color: #202124;
          margin: 0;
          font-weight: 500;
        ">${organizer.name}</p>
      `;
    })()}
  </div>
</div>
</div>

<!-- Edit/Delete Buttons (only for event creator) OR Leave Button (for participants) -->
${(() => {
  const currentUserId = window.currentAppUser?.uid;
  const isOrganizer = eventData.creatorId === currentUserId;
  const isParticipant = eventData.participants?.includes(currentUserId) && !isOrganizer;
  
  if (isOrganizer) {
    // Show Edit and Delete buttons for organizer
    return `
      <div id="actionButtons" style="
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 16px 24px;
        background: white;
        border-top: 1px solid #e5e7eb;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
      ">
        <div style="
          display: flex;
          gap: 12px;
          justify-content: center;
        ">
          ${!hasEnded ? `
          <!-- Edit Button -->
          <button
            onclick="enableEditMode('${eventData.eventId}')"
            style="
              width: 140px;
              padding: 12px 20px;
              background: #f8f9fa;
              color: rgba(131, 131, 131, 1);
              border: 2px solid rgba(131, 131, 131, 1);
              border-radius: 30px;
              font-size: 15px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              box-shadow: rgba(0, 0, 0, 0.4) 2px 2px 6px;
            "
            onmouseover="this.style.background='#ddddddff';"
            onmouseout="this.style.background='#f8f9fa';"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
            Edit
          </button>
          ` : ''}
          
          <!-- Delete Button -->
          <button
            onclick="deleteEventConfirm('${eventData.eventId}')"
            style="
              width: 140px;
              padding: 12px 20px;
              background: #fef7f7;
              color: rgba(255, 23, 0, 1);
              border: 2px solid rgba(255, 23, 0, 1);
              border-radius: 30px;
              font-size: 15px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              box-shadow: rgba(0, 0, 0, 0.4) 2px 2px 6px;
            "
            onmouseover="this.style.background='#fa050533';"
            onmouseout="this.style.background='#fef7f7';"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
            </svg>
            Delete
          </button>
        </div>
      </div>
    `;
  } else if (isParticipant) {
    // Show Leave button for participant
    return `
<!-- Report Event Button (above bottom section) -->
      <div id="reportEventBtnContainer" style="
        position: absolute;
        bottom: 80px;
        right: 24px;
        z-index: 1;
      ">
        <button
          id="reportEventBtn"
          onclick="window.showReportModal('event', '${eventData.eventId}', '${eventData.title}')"
          style="
            background: none;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 6px;
            color: #ea4335;
            font-size: 14px;
            padding: 15px 0;
            border-radius: 8px;
            transition: background 0.2s; 
          "
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
          Report Event
        </button>
      </div>
      <script>
        (async () => {
          const reported = await window.checkAlreadyReported('event', '${eventData.eventId}');
          const btn = document.getElementById('reportEventBtn');
          if (reported && btn) {
            btn.onclick = null;
            btn.style.cursor = 'default';
            btn.style.color = '#9ca3af';
            btn.style.opacity = '0.7';
            btn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg> Already reported';
          }
        })();
      </script>
      
      <!-- Bottom Action Buttons -->
      <div id="actionButtons" style="
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 16px 24px;
        background: white;
        border-top: 1px solid #e5e7eb;
        box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
      ">
        <div style="
          display: flex;
          justify-content: center;
        ">
          <!-- Leave Button -->
          <button
            onclick="window.leaveEventConfirm('${eventData.eventId}')"
            style="
              width: 280px;
              padding: 12px 20px;
              background: white;
              color: rgba(255, 23, 0, 1);
              border: 2px solid rgba(255, 23, 0, 1);
              border-radius: 30px;
              font-size: 15px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              box-shadow: rgba(0, 0, 0, 0.4) 2px 2px 6px;
            "
            onmouseover="this.style.background='#fef7f7';"
            onmouseout="this.style.background='white';"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            Leave Event
          </button>
        </div>
      </div>
    `;
  } else {
    // Non-participant, non-organizer - no buttons
return `<!-- Report Event Button (for non-joined users) -->
        <div style="
          display: flex;
          justify-content: center;
          padding: 20px 24px;
          border-top: 1px solid #e0e0e0;
          margin-top: auto;
        ">
          <button
            id="reportEventBtnNonJoined"
            onclick="window.showReportModal('event', '${eventData.eventId}', '${eventData.title}')"
            style="
              background: none;
              border: none;
              cursor: pointer;
              display: flex;
              align-items: center;
              gap: 8px;
              color: #ea4335;
              font-size: 14px;
              font-weight: 600;
              padding: 8px 16px;
              border-radius: 8px;
              transition: background 0.2s;
            "
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            Report Event
          </button>
        </div>
        <script>
          (async () => {
            const reported = await window.checkAlreadyReported('event', '${eventData.eventId}');
            const btn = document.getElementById('reportEventBtnNonJoined');
            if (reported && btn) {
              btn.onclick = null;
              btn.style.cursor = 'default';
              btn.style.color = '#9ca3af';
              btn.style.opacity = '0.7';
              btn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg> Already reported';
            }
          })();
        </script>`;
  }
})()}
`;

    document.body.appendChild(panel);

    // Slide in animation
    setTimeout(() => {
      panel.style.right = '0';
    }, 10);

// Start countdown if exists
const checkNow = new Date();
const checkStartDate = new Date(eventData.startDateTime);
const timeUntilStart = checkStartDate - checkNow;
const daysUntilStart = timeUntilStart / (1000 * 60 * 60 * 24);
const showCountdown = daysUntilStart > 0 && daysUntilStart <= 30;

console.log('Countdown check:', {
  eventStartTime: eventData.startDateTime,
  daysUntilStart: daysUntilStart,
  showCountdown: showCountdown
});

if (showCountdown) {
  const uniqueEventId = `${eventData.placeId}_${eventData.startDateTime}`.replace(/[^a-zA-Z0-9]/g, '_');
  // Check if elements exist
  const testElement = document.getElementById(`countdown_details_${uniqueEventId}_days`);
  
// Initial update
window.updateCountdown(`details_${uniqueEventId}`, eventData.startDateTime);

// Set up interval
const countdownInterval = setInterval(() => {
  const stillActive = window.updateCountdown(`details_${uniqueEventId}`, eventData.startDateTime);
  if (!stillActive) {
    clearInterval(countdownInterval);
  }
}, 1000);
  
  // Store interval for cleanup
  if (!window.eventIntervals) {
    window.eventIntervals = [];
  }
  window.eventIntervals.push(countdownInterval);
}

// CLEANUP: Clear any existing intervals for this panel
if (window.eventDetailsInterval) {
  clearInterval(window.eventDetailsInterval);
  window.eventDetailsInterval = null;
}

// Add real-time status update interval
const statusUpdateInterval = setInterval(() => {
  
  // Check if panel still exists
  const panel = document.getElementById('eventDetailsPanel');
  if (!panel) {
    clearInterval(statusUpdateInterval);
    return;
  }

  const status = window.calculateEventStatus(eventData.startDateTime, eventData.endDateTime);
  const newStatusColor = status.statusColor;
  const newStatusText = status.statusText;
  const newShouldPulse = status.shouldPulse;
  
  // Update status circle and text
  const statusCircle = panel.querySelector('[data-status-circle]');
  const statusPulse = panel.querySelector('[data-status-pulse]');
  const statusText = panel.querySelector('[data-status-text]');
  const placeCard = panel.querySelector('[data-place-card]');
  const titleElement = panel.querySelector('[data-event-title]');
  const placeCardPulse = panel.querySelector('[data-place-pulse]');

  if (statusCircle) statusCircle.style.background = newStatusColor;
  if (statusText) {
    statusText.textContent = newStatusText;
    statusText.style.color = newStatusColor;
  }
  if (placeCard) placeCard.style.borderColor = newStatusColor;
  if (titleElement) titleElement.style.color = newStatusColor;

  // Update pulse visibility and color
  if (statusPulse) {
    statusPulse.style.display = newShouldPulse ? 'block' : 'none';
    statusPulse.style.borderColor = newStatusColor;
  }

  // Update place card pulse color
  if (placeCardPulse) {
    placeCardPulse.style.borderColor = newStatusColor;
    placeCardPulse.style.display = newShouldPulse ? 'block' : 'none';
  }
  
// Hide countdown when event starts or ends
  if (status.hasStarted) {
    const countdownElement = panel.querySelector('[data-countdown-timer]');
    if (countdownElement) {
      countdownElement.style.display = 'none';
    }
    
    // Update hasStarted dataset so edit mode knows
    panel.dataset.hasStarted = 'true';
    
    // If currently in edit mode, disable/remove start date fields
    const startDateInput = document.getElementById('edit-start-date');
    const startTimeInput = document.getElementById('edit-start-time');
    if (startDateInput || startTimeInput) {
      // Find the start row and restore to non-editable display
      const startRow = panel.querySelector('[data-start-row]');
      const startValueP = startRow?.querySelector('[data-start-value]');
      if (startValueP && (startDateInput || startTimeInput)) {
        // Restore to read-only display using the original start date
        const originalStart = panel.dataset.startDateTime;
        if (originalStart) {
          const d = new Date(originalStart.replace(/[+-]\d{2}:\d{2}$/, '').replace('Z', ''));
          const dateStr = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
          const timeStr = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
          startValueP.innerHTML = `<span style="color: #202124; font-weight: 500;">${dateStr}, ${timeStr}</span>`;
        }
        // Remove the edit icon added flag so it won't be re-added
        if (startRow) startRow.dataset.editIconAdded = 'true';
        
        // Update save button state since the start fields are gone
        if (window.updateSaveButtonState) window.updateSaveButtonState();
      }
    }
  }
  
  // Update panel border color
  panel.style.borderLeftColor = newStatusColor;
}, 1000);

// Store it globally for cleanup
window.eventDetailsInterval = statusUpdateInterval;

// Store interval for cleanup
if (!window.eventIntervals) {
  window.eventIntervals = [];
}
window.eventIntervals.push(statusUpdateInterval);

window.setupEventRealtimeUpdates(eventData.eventId);
// Set up real-time status updates for participants
  const allParticipantIds = [eventData.creatorId, ...(eventData.participants || [])];
  window.setupParticipantStatusUpdates(allParticipantIds);
};

window.showEventDetailsPanel = showEventDetailsPanel;